import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaApple, FaGooglePlay } from "react-icons/fa";

const API_BASE = "https://kinalma-app-696ccda80bc9.herokuapp.com";

// TODO: replace with the real store listings once they are live.
const APP_STORE_URL = "#";
const PLAY_STORE_URL = "#";

const JoinAlbum = () => {
  const { code } = useParams();
  const [status, setStatus] = useState("loading"); // loading | success | invalid | error
  const [album, setAlbum] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAlbum = async () => {
      try {
        const response = await fetch(`${API_BASE}/checkAlbumAccessCode`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessCode: code }),
        });
        const data = await response.json();
        if (cancelled) return;

        if (response.ok && data.album) {
          setAlbum(data.album);
          setStatus("success");
        } else if (response.status === 401) {
          setStatus("invalid");
        } else {
          setStatus("error");
        }
      } catch (err) {
        if (!cancelled) setStatus("error");
      }
    };

    if (code) {
      fetchAlbum();
    } else {
      setStatus("invalid");
    }

    return () => {
      cancelled = true;
    };
  }, [code]);

  const authorName = album?.author
    ? [album.author.firstName, album.author.lastName].filter(Boolean).join(" ").trim() ||
      album.author.name ||
      album.author.email
    : null;

  const albumImage =
    album?.coverImage ||
    album?.coverImageUrl ||
    album?.image ||
    album?.imageUrl ||
    album?.cover ||
    album?.thumbnail ||
    album?.photo ||
    null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Nav */}
      <nav className="sticky top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="container mx-auto flex items-center py-4 px-6">
          <Link to="/" className="flex items-center">
            <img src="/Favicon.png" alt="KinAlma" className="h-10" />
          </Link>
          <div className="ml-auto">
            <Link
              to="/privacy-policy"
              className="text-sm font-medium text-gray-500 hover:text-pink-400 transition-colors"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </nav>

      {/* Main */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {status === "loading" && <LoadingState />}
        {status === "invalid" && <InvalidState code={code} />}
        {status === "error" && <ErrorState />}
        {status === "success" && album && (
          <SuccessState
            album={album}
            authorName={authorName}
            code={code}
            albumImage={albumImage}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()}{" "}
            KinAlma. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-pink-400 transition-colors">
              Privacy
            </Link>
            <a
              href="mailto:accounts@kinalma.com"
              className="text-sm text-gray-500 hover:text-pink-400 transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-24">
    <div className="h-12 w-12 rounded-full border-4 border-pink-200 border-t-pink-500 animate-spin" />
    <p className="mt-6 text-gray-500 text-sm">Looking up your invitation…</p>
  </div>
);

const InvalidState = ({ code }) => (
  <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-10 text-center">
    <h1 className="text-3xl font-bold text-gray-900">This invitation isn't valid</h1>
    <p className="mt-4 text-gray-600 leading-relaxed">
      We couldn't find an album with the access code{" "}
      <span className="font-mono font-semibold text-gray-800">{code || "—"}</span>. The code may
      have been mistyped or the album may no longer be available.
    </p>
    <div className="mt-8 flex items-center justify-center">
      <Link
        to="/"
        className="inline-flex h-11 items-center justify-center rounded-md bg-indigo-600 px-6 font-medium text-white hover:bg-indigo-700 transition-colors"
      >
        Go to <span className="ml-1 font-semibold">KinAlma</span>
      </Link>
    </div>
  </div>
);

const ErrorState = () => (
  <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-10 text-center">
    <h1 className="text-3xl font-bold text-gray-900">Something went wrong</h1>
    <p className="mt-4 text-gray-600 leading-relaxed">
      We couldn't load this invitation right now. Please check your connection and try again in a
      moment.
    </p>
    <div className="mt-8 flex items-center justify-center">
      <button
        onClick={() => window.location.reload()}
        className="inline-flex h-11 items-center justify-center rounded-md bg-indigo-600 px-6 font-medium text-white hover:bg-indigo-700 transition-colors"
      >
        Try again
      </button>
    </div>
  </div>
);

const SuccessState = ({ album, authorName, code, albumImage }) => {
  const storyCount = Array.isArray(album.stories) ? album.stories.length : 0;
  const guestCount = Array.isArray(album.guests) ? album.guests.length : 0;

  return (
    <div className="space-y-8">
      {/* Invitation hero */}
      <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-br from-pink-50 via-white to-indigo-50 px-6 sm:px-10 py-10 text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-pink-500">
            You're invited
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-gray-900">
            Join the <span className="text-pink-500">{album.name}</span> memories
          </h1>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-xl mx-auto">
            {authorName ? (
              <>
                {authorName} wants to share stories with you on{" "}
                <span className="text-pink-500 font-semibold">KinAlma</span>.
              </>
            ) : (
              <>
                You've been invited to share stories on{" "}
                <span className="text-pink-500 font-semibold">KinAlma</span>.
              </>
            )}
          </p>
          {album.description && (
            <p className="mt-4 text-gray-500 italic max-w-xl mx-auto">"{album.description}"</p>
          )}

          {(storyCount > 0 || guestCount > 0) && (
            <div className="mt-8 inline-flex items-center gap-6 rounded-full bg-white/70 backdrop-blur-sm px-5 py-3 shadow-sm">
              <Stat label={storyCount === 1 ? "story" : "stories"} value={storyCount} />
              <div className="h-6 w-px bg-gray-200" />
              <Stat label={guestCount === 1 ? "member" : "members"} value={guestCount} />
            </div>
          )}
        </div>

        {/* Album cover */}
        {albumImage && (
          <div className="border-t border-gray-100 bg-gray-50">
            <div className="aspect-square w-full overflow-hidden">
              <img
                src={albumImage}
                alt={album.name ? `${album.name} album cover` : "Album cover"}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}
      </section>

      {/* Steps */}
      <section className="bg-white rounded-2xl shadow-sm p-6 sm:p-10">
        <h2 className="text-2xl font-bold text-gray-900 text-center">How to join</h2>
        <p className="mt-2 text-gray-500 text-center">Three quick steps and you're in.</p>

        <ol className="mt-8 space-y-5">
          <Step
            number={1}
            title={
              <>
                Download the <span className="text-pink-500">KinAlma</span> app
              </>
            }
            body={
              <>
                Get KinAlma from the App Store
               on your phone.
              </>
            }
          />
          <Step
            number={2}
            title="Sign up or log in"
            body="Create a free account (or sign in if you already have one)."
          />
          <Step
            number={3}
            title={
              <>
                Tap <span className="font-semibold text-pink-500">Join Album</span> and enter the code
              </>
            }
            body={
              <>
                Use the access code{" "}
                <span className="font-mono font-semibold text-gray-800">{code}</span> to unlock the
                album.
              </>
            }
          />
        </ol>

        {/* Store buttons */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <StoreButton
            href={APP_STORE_URL}
            icon={<FaApple className="h-7 w-7" />}
            label="Download on the"
            store="App Store"
          />
        </div>
      </section>

      {/* Closing line */}
      <p className="text-center text-gray-500 text-sm">
        Already have the app? Open{" "}
        KinAlma, tap{" "}
        <span className="font-semibold text-gray-700">Join Album</span>, and enter{" "}
        <span className="font-mono font-semibold text-gray-800">{code}</span>.
      </p>
    </div>
  );
};

const Stat = ({ value, label }) => (
  <div className="flex items-baseline gap-1.5">
    <span className="text-xl font-bold text-gray-900">{value}</span>
    <span className="text-sm text-gray-500">{label}</span>
  </div>
);

const Step = ({ number, title, body }) => (
  <li className="flex gap-4 items-start">
    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-pink-100 text-pink-600 font-bold flex items-center justify-center">
      {number}
    </div>
    <div className="pt-1">
      <p className="text-lg font-semibold text-gray-900">{title}</p>
      <p className="mt-1 text-gray-600 leading-relaxed">{body}</p>
    </div>
  </li>
);

const StoreButton = ({ href, icon, label, store }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-3 rounded-xl bg-gray-900 hover:bg-black text-white px-5 py-3 w-full sm:w-auto justify-center transition-colors"
  >
    {icon}
    <div className="flex flex-col leading-tight text-left">
      <span className="text-[10px] uppercase tracking-wide text-gray-300">{label}</span>
      <span className="text-base font-semibold">{store}</span>
    </div>
  </a>
);

export default JoinAlbum;
